<script setup lang="ts">
import { computed } from 'vue';

import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { AccommodationSelectorType } from '@apps/main/new-desktop/modules/accommodationSelector';
import { AccommodationSelector } from '@apps/main/new-desktop/modules/accommodationSelector';
import type { AvailableItineraryAccommodationSegment } from '@apps/main/new-desktop/pages/tripOverview/models';

import { AppSnapScroll } from '@libs/ui';

import { ProductCardTopBarType } from '../../../models';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ChangeProductLink from '../ChangeProductLink.vue';

import AccommodationSegmentHeader from './AccommodationSegmentHeader.vue';
import AlternativeHotelCardMobile from './mobile/AlternativeHotelCardMobile.vue';
import AccommodationProductCardMobile from './mobile/AccommodationProductCardMobile.vue';
import GenericHotelCard from './GenericHotelCard.vue';
import AccommodationCarouselDesktop from './AccommodationCarouselDesktop.vue';

interface AvailableAccommodationSegmentProps {
  segment: AvailableItineraryAccommodationSegment;
  printMode: boolean;
  ingredientIndex: number;
}

const props = defineProps<AvailableAccommodationSegmentProps>();

interface AvailableAccommodationSegmentEmits {
  (eventName: 'add-filter'): void;
  (eventName: 'hotel-selection-navigation-triggered'): void;
  (eventName: 'remove-filter', value: { type: HotelFilter; id?: string }): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'show-rooms'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (eventName: 'toggle-accommodation', value: AccommodationSelectorType): void;
}

const emit = defineEmits<AvailableAccommodationSegmentEmits>();

const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();

//NOTE: duplicated
const currentSelectedOrderType = computed<
  AccommodationSelectorType | undefined
>(() => {
  if (props.segment.barType === ProductCardTopBarType.Selected) {
    return 'popularAccommodation';
  }

  return props.segment.barType === ProductCardTopBarType.Suggestion
    ? 'popularAccommodation'
    : 'cheapestAccommodation';
});

function onNavigationTriggered(): void {
  statsigEventsStore.logTripOverviewSeeAllHotelsClicked();
  emit('hotel-selection-navigation-triggered');
}
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    class="available-accommodation-segment"
  >
    <template #dropdown>
      <AccommodationSelector
        v-if="currentSelectedOrderType && !props.printMode"
        :is-there-selected-accommodation="
          props.segment.barType === ProductCardTopBarType.Selected
        "
        :selected-option="currentSelectedOrderType"
        :disabled="props.printMode"
        @toggle-accommodation="emit('toggle-accommodation', $event)"
      />
    </template>
    <template #header>
      <AccommodationSegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @add-filter="emit('add-filter')"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <div class="content-wrapper">
        <div
          v-if="browserStore.isSmallerThan600px && !props.printMode"
          class="accommodation-cards-mobile-wrapper"
        >
          <AppSnapScroll direction="X" class="accommodation-cards-mobile">
            <template #scrollable>
              <div class="card-wrapper">
                <AccommodationProductCardMobile
                  :product-card-data="props.segment.productCardData"
                  :context="props.segment.context"
                  @show-details="emit('show-details')"
                  @show-rooms="emit('show-rooms')"
                />
              </div>
              <div
                v-for="(hotel, index) in props.segment.alternativeHotels"
                :key="hotel.hotelCode"
                class="card-wrapper"
              >
                <AlternativeHotelCardMobile
                  class="card"
                  :hotel="hotel"
                  :position="index"
                  :ingredient-index="props.segment.context.ingredientIndex"
                  :date="props.segment.productCardData.date"
                />
              </div>
              <div class="card-wrapper">
                <GenericHotelCard
                  :ingredient-index="props.segment.context.ingredientIndex"
                />
              </div>
            </template>
          </AppSnapScroll>
          <ChangeProductLink
            v-if="!props.printMode"
            class="change-product-link"
            :link-context="{
              segmentType: props.segment.segmentType,
              ingredientIndex: props.ingredientIndex,
            }"
            @navigation-triggered="onNavigationTriggered"
          />
        </div>
        <template v-if="!browserStore.isSmallerThan600px">
          <AccommodationCarouselDesktop
            :segment="props.segment"
            :print-mode="props.printMode"
          >
            <ChangeProductLink
              v-if="!props.printMode"
              class="change-product-link"
              :link-context="{
                segmentType: props.segment.segmentType,
                ingredientIndex: props.ingredientIndex,
              }"
              @navigation-triggered="onNavigationTriggered"
            />
          </AccommodationCarouselDesktop>
        </template>
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.available-accommodation-segment {
  .content-wrapper {
    display: flex;
    flex-flow: column;
    gap: var(--spacing-16);

    .change-product-link {
      align-self: flex-end;
    }

    .accommodation-cards-mobile-wrapper {
      display: grid;
      gap: var(--spacing-16);

      .single-card {
        width: 100%;
        padding-left: var(--spacing-12);
        padding-right: var(--spacing-12);
      }

      .change-product-link {
        margin-left: auto;
      }

      .accommodation-cards-mobile {
        display: grid;
        grid-auto-flow: column;
        overscroll-behavior-x: contain;

        .card-wrapper {
          width: calc(100% - 30px);
          padding-left: var(--spacing-12);

          &:last-child {
            padding-right: var(--spacing-12);
          }
        }
      }
    }
  }
}
</style>
